import { useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { trackAeoLondonConversion, trackExitIntent } from "@/lib/analytics";

const STORAGE_KEY = "aeo-london-exit-intent-next-eligible";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function isEligibleForPrompt(): boolean {
  try {
    const nextEligible = Number(localStorage.getItem(STORAGE_KEY) ?? 0);
    return !Number.isFinite(nextEligible) || Date.now() >= nextEligible;
  } catch {
    return false;
  }
}

function scheduleNextPrompt(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + SEVEN_DAYS_MS));
  } catch {
    // Storage is optional. The visitor can still close the accessible prompt.
  }
}

export default function AeoLondonExitIntent() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  const close = () => {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (window.matchMedia("(max-width: 1023px)").matches || !isEligibleForPrompt()) return;

    const onMouseOut = (event: MouseEvent) => {
      if (event.clientY > 0 || event.relatedTarget) return;
      scheduleNextPrompt();
      triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpen(true);
      trackExitIntent({ page_slug: "aeo_agency_london", scroll_depth_at_exit: 0 });
      trackAeoLondonConversion({ action: "exit_intent_shown", placement: "desktop_exit_intent" });
    };

    document.addEventListener("mouseout", onMouseOut, { once: true });
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] hidden items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm lg:flex" role="presentation">
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="aeo-exit-title" className="relative w-full max-w-md rounded-2xl border border-[#FF8C00]/35 bg-[#101722] p-6 shadow-2xl">
        <button ref={closeRef} type="button" onClick={close} className="absolute right-4 top-4 inline-flex size-11 items-center justify-center rounded-lg text-slate-300 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FF8C00]" aria-label="Close AEO prompt">
          <X className="size-5" aria-hidden="true" />
        </button>
        <p className="pr-12 text-xs font-bold uppercase tracking-[0.14em] text-[#ffad4d]">Before you go</p>
        <h2 id="aeo-exit-title" className="mt-3 pr-8 font-serif text-3xl text-white">See how AI engines describe your brand.</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">The Free AI Visibility Score is a high-level website baseline. No card needed.</p>
        <a href="/free-ai-growth-audit/" data-score-direct="true" onClick={() => trackAeoLondonConversion({ action: "exit_score_click", placement: "desktop_exit_intent" })} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#111827] transition hover:bg-[#ffad4d] focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 focus:ring-offset-[#101722]">
          Check my AI visibility<ArrowRight className="size-4" aria-hidden="true" />
        </a>
      </section>
    </div>
  );
}
